import { getAuthtoken } from "./helper";
import { toaster } from "./toast";
import { clearSessionAndRedirect } from "./session";
import { createSseFrameParser, SSE_CONTENT_TYPE, FRAME_TYPE } from "./sseFrameParser";

const isEventStream = (response) => (response.headers.get("content-type") || "").includes(SSE_CONTENT_TYPE);

// route a parsed frame: toast frames go to the toaster, everything else to the consumer
const dispatchFrame = (frame, onUpdate) => {
  if (frame.type === FRAME_TYPE.TOAST) {
    toaster({ message: frame.message, variant: frame.isError ? "error" : "success" });
    return;
  }
  onUpdate(frame);
};

const consumeEventStream = async ({ response, onUpdate }) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseFrameParser();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true })).forEach((frame) => dispatchFrame(frame, onUpdate));
  }
};

// a non-stream response is a terminal outcome; surface an error as a terminal error update + toast
const consumeJsonOutcome = async ({ response, onUpdate }) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.error) {
    const message = payload.error || payload.message || "Request failed";
    toaster({ message, variant: "error" });
    onUpdate({ done: true, error: true, message });
    return;
  }
  onUpdate({ done: true, ...payload });
};

// a frame is terminal once it reports a done flag or an explicit success outcome
const isTerminalFrame = (frame) => Boolean(frame?.done) || frame?.success !== undefined;

/**
 * Imperatively read an Update Stream over a fetch-based connection. Sends the auth Bearer header,
 * forwards every frame to onUpdate, routes toast frames to the toaster, and resolves with the
 * terminal frame once the stream (SSE or plain JSON) completes. Resolves null after a 401 redirect.
 * @param {object} params
 * @param {string} params.url
 * @param {string} [params.method]
 * @param {object} [params.body]
 * @param {object} [params.headers]
 * @param {(frame: object) => void} [params.onUpdate]
 * @param {AbortSignal} [params.signal]
 */
export const readUpdateStream = async ({ url, method = "GET", body, headers, onUpdate = () => {}, signal }) => {
  const response = await fetch(url, {
    method,
    headers: { Authorization: `Bearer ${getAuthtoken()}`, "Content-Type": "application/json", ...headers },
    body: body ? JSON.stringify(body) : undefined,
    signal
  });

  if (response.status === 401) {
    clearSessionAndRedirect();
    return null;
  }

  let terminalFrame;
  const forward = (frame) => {
    onUpdate(frame);
    if (isTerminalFrame(frame)) terminalFrame = frame;
  };

  if (isEventStream(response)) {
    await consumeEventStream({ response, onUpdate: forward });
  } else {
    await consumeJsonOutcome({ response, onUpdate: forward });
  }

  return terminalFrame;
};
